import React, { useEffect } from 'react';
import { useCookieConsentContext } from '../../context/CookieConsentContext';

type ServiceCategory = 'analytics' | 'marketing';

interface ConditionalScriptLoaderProps {
  category: ServiceCategory;
  serviceName: string; // For logging purposes
  initializeService: () => void; // Function to call if consent is given
  cleanupService?: () => void; // Optional: Function to call if consent is revoked or component unmounts
}

const ConditionalScriptLoader: React.FC<ConditionalScriptLoaderProps> = ({
  category,
  serviceName,
  initializeService,
  cleanupService,
}) => {
  const { preferences, isLoading, hasMadeChoice } = useCookieConsentContext();

  useEffect(() => {
    if (isLoading) {
      // Still loading preferences, do nothing yet
      return;
    }    // Check if consent has been given for the specific category
    // We also check hasMadeChoice to ensure we don't initialize if user hasn't interacted with banner yet
    // (unless preferences were loaded from storage, in which case hasMadeChoice would be true)
    const consentGiven = preferences[category] && hasMadeChoice;    if (consentGiven) {
      console.log(`Cookie consent given for ${category}. Initializing ${serviceName}.`);
      initializeService();
    } else {
      // Do not log in development mode as it's expected that services won't be initialized
      if (process.env.NODE_ENV !== 'development') {
        console.log(`Cookie consent NOT given for ${category} or choice not made. ${serviceName} will not be initialized.`);
      }
      // If consent was previously given but now revoked, cleanup if possible
      if (cleanupService && preferences.lastSet) { // Assuming lastSet indicates a previous choice might have existed
         // More specific logic might be needed here if we want to detect a change from true to false
         // For now, if not consented, and cleanup is available, it implies it might need cleanup.
         // However, this cleanup on preference change from true->false is tricky without more state.
         // The primary goal is to prevent initialization.
         // cleanupService(); // This might be too aggressive or needs better conditions.
      }
    }

    return () => {
      // Cleanup when the component unmounts, if a cleanup function is provided
      // and if the service was potentially initialized.
      // This is a basic cleanup; more sophisticated cleanup might be needed depending on the service.
      if (cleanupService && preferences[category]) { // Only cleanup if it might have been initialized
        console.log(`Cleaning up ${serviceName} due to component unmount or consent change.`);
        cleanupService();
      }
    };
    // Re-run effect if loading status changes, or if preferences for the category change, or if a choice is made.
  }, [isLoading, preferences, category, serviceName, initializeService, cleanupService, hasMadeChoice]);

  return null; // This component does not render anything itself
};

export default ConditionalScriptLoader;
